import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Box,
    Typography,
    CircularProgress,
    InputAdornment,
    IconButton,
    Card,
    CardContent,
    Stack,
    Divider,
    TablePagination
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Search as SearchIcon, Close as CloseIcon } from '@mui/icons-material';
import imputableService, { Imputable } from '../services/imputableService';

interface ImputableSearchProps {
    open: boolean;
    onClose: () => void;
    onSelect: (imputable: Imputable) => void;
    /**
     * Tipo de movimiento para filtrar por visibleEn:
     * - 'I'  -> visibleEn === 'IVarios' o 'Ambos'
     * - 'E'  -> visibleEn === 'EVarios' o 'Ambos'
     * - undefined -> sin filtro adicional
     */
    tipoMovimientoIE?: 'I' | 'E';
}

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

const ImputableSearch: React.FC<ImputableSearchProps> = ({ open, onClose, onSelect, tipoMovimientoIE }) => {
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [searchTerm, setSearchTerm] = useState('');
    const [imputablesFiltrados, setImputablesFiltrados] = useState<Imputable[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    const loadImputables = useCallback(async (pageNum: number = page, pageSize: number = rowsPerPage, search: string = searchTerm) => {
        try {
            setLoading(true);
            setError(null);
            const result = await imputableService.getImputablesSearchPaged({
                pageNumber: pageNum + 1,
                pageSize,
                searchTerm: search.trim() || undefined,
                tipoMovimientoIE
            });

            // El filtro por VisibleEn ahora se aplica en el backend para que totalCount y la paginación coincidan.
            setImputablesFiltrados(result.items);
            setTotalCount(result.totalCount);
            if (result.totalCount === 0) {
                setError('No se encontraron cuentas contables.');
            }
        } catch (err: any) {
            setError(`Error al cargar cuentas contables: ${err.message || 'Error desconocido'}`);
            setImputablesFiltrados([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    }, [tipoMovimientoIE]);

    const handleSearch = useCallback(() => {
        setPage(0);
        loadImputables(0, rowsPerPage, searchTerm);
    }, [searchTerm, rowsPerPage, loadImputables]);

    const handleChangePage = useCallback((_event: unknown, newPage: number) => {
        setPage(newPage);
        loadImputables(newPage, rowsPerPage, searchTerm);
    }, [rowsPerPage, searchTerm, loadImputables]);

    const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
        const newSize = parseInt(event.target.value, 10);
        setRowsPerPage(newSize);
        setPage(0);
        loadImputables(0, newSize, searchTerm);
    }, [searchTerm, loadImputables]);

    const handleSelect = (imputable: Imputable) => {
        onSelect(imputable);
        onClose();
    };

    const handleClose = () => {
        setSearchTerm('');
        setImputablesFiltrados([]);
        setError(null);
        setPage(0);
        onClose();
    };

    useEffect(() => {
        if (open) {
            setPage(0);
            loadImputables(0, rowsPerPage, searchTerm);
        } else {
            setSearchTerm('');
            setImputablesFiltrados([]);
            setError(null);
            setPage(0);
        }
    }, [open, tipoMovimientoIE, loadImputables]);

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            fullScreen={isSmallScreen}
            sx={{
                '& .MuiDialog-paper': {
                    margin: { xs: 0, sm: 2 },
                    maxHeight: { xs: '100%', sm: '90vh' },
                    height: { xs: '100%', sm: 'auto' }
                }
            }}
        >
            <DialogTitle sx={{ pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                        Buscar Cuenta Contable
                    </Typography>
                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>
            
            <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 1, sm: 2 } }}>
                <Box sx={{ mb: 2 }}>
                    <TextField
                        fullWidth
                        variant="outlined"
                        placeholder="Número de cuenta o nombre"
                        inputProps={{ 'aria-label': 'Buscar cuenta contable' }}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                                handleSearch();
                            }
                        }}
                        size={isSmallScreen ? 'small' : 'medium'}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={handleSearch} disabled={loading} edge="end">
                                        <SearchIcon />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                        sx={{ mb: 1.5 }}
                    />
                </Box>

                {error && (
                    <Typography color="error" sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                        {error}
                    </Typography>
                )}

                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                        <CircularProgress />
                    </Box>
                )}

                {!loading && imputablesFiltrados.length > 0 && (
                    <>
                        {isSmallScreen ? (
                            // Vista de tarjetas para móviles
                            <Box sx={{ maxHeight: '50vh', overflowY: 'auto', pr: 1 }}>
                                <Stack spacing={2}>
                                    {imputablesFiltrados.map((imputable) => (
                                        <Card 
                                            key={imputable.nroCuenta} 
                                            sx={{ 
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    boxShadow: 3
                                                }
                                            }}
                                            onClick={() => handleSelect(imputable)}
                                        >
                                            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                                    <Typography variant="subtitle2" color="primary" fontWeight="bold">
                                                        Nro: {imputable.nroCuenta}
                                                    </Typography>
                                                    <Button
                                                        variant="contained"
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSelect(imputable);
                                                        }}
                                                        sx={{ minWidth: 'auto', px: 2 }}
                                                    >
                                                        Seleccionar
                                                    </Button>
                                                </Box>
                                                <Divider sx={{ my: 1.5 }} />
                                                <Stack spacing={1}>
                                                    <Box>
                                                        <Typography variant="caption" color="text.secondary" display="block">
                                                            Nombre
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight="medium">
                                                            {imputable.nomCuenta?.trim() || '-'}
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </Stack>
                            </Box>
                        ) : (
                            // Vista de tabla para pantallas grandes
                            <TableContainer 
                                component={Paper} 
                                sx={{ 
                                    maxHeight: 400,
                                    '& .MuiTableCell-root': {
                                        py: 1.5,
                                        fontSize: '0.875rem'
                                    }
                                }}
                            >
                                <Table stickyHeader size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: 100 }}>Nro. Cuenta</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', minWidth: 300 }}>Nombre</TableCell>
                                            <TableCell sx={{ fontWeight: 'bold', width: 120 }} align="center">Acción</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {imputablesFiltrados.map((imputable) => (
                                            <TableRow 
                                                key={imputable.nroCuenta} 
                                                hover
                                                sx={{ cursor: 'pointer' }}
                                                onClick={() => handleSelect(imputable)}
                                            >
                                                <TableCell>{imputable.nroCuenta}</TableCell>
                                                <TableCell>{imputable.nomCuenta?.trim() || '-'}</TableCell>
                                                <TableCell align="center">
                                                    <Button
                                                        variant="outlined"
                                                        size="small"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleSelect(imputable);
                                                        }}
                                                    >
                                                        Seleccionar
                                                    </Button>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                        <TablePagination
                            component="div"
                            count={totalCount}
                            page={page}
                            onPageChange={handleChangePage}
                            rowsPerPage={rowsPerPage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
                            labelRowsPerPage="Filas por página:"
                            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
                            sx={{ mt: 1 }}
                        />
                    </>
                )}

                {!loading && imputablesFiltrados.length === 0 && !error && (
                    <Typography color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
                        No se encontraron cuentas contables
                    </Typography>
                )}
            </DialogContent>

            <DialogActions sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 2 }, flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
                <Button 
                    onClick={handleClose} 
                    fullWidth={isSmallScreen}
                    size={isSmallScreen ? 'medium' : 'small'}
                    variant="outlined"
                >
                    CANCELAR
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ImputableSearch;

